import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import Breadcrumb from '../components/Breadcrumb';
import Pagination from '../components/main/Pagination';
import useSuggestionsApi from '../api/useSuggestionsApi';
import { debounce } from '../helper/debounce';
import CommonLoader from '../components/loaders/CommonLoader';
import { Avatar, Dialog } from '@mui/material';
import dayjs from 'dayjs';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const StatusBadge = ({ status }) => {
    const statusStyles = {
        approved: {
            text: "Approved",
            className:
                "bg-success-focus text-success-600 border border-success-main px-24 py-4 radius-4 fw-medium text-sm",
        },
        pending: {
            text: "Pending",
            className:
                "bg-warning-focus text-warning-600 border border-warning-main px-24 py-4 radius-4 fw-medium text-sm",
        },
        rejected: {
            text: "Rejected",
            className:
                "bg-danger text-white border border-danger px-24 py-4 radius-4 fw-medium text-sm",
        },
    };

    const statusInfo = statusStyles[status] || {
        text: "Inactive",
        className:
            "bg-gray-200 text-gray-600 border border-gray-400 px-24 py-4 radius-4 fw-medium text-sm",
    };

    return <span className={statusInfo.className}>{statusInfo.text}</span>;
};

const SuggestionsPage = () => {
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { getSuggestionsData, loading, approveRejectSuggestion } = useSuggestionsApi();
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total_pages, setTotalPages] = useState(1);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [openApprove, setOpenApprove] = useState(false);
    const [openReject, setOpenReject] = useState(false);
    const [reason, setReason] = useState("");
    const { isActionAllowed } = useAuth();
    const handleApprove = (id) => {
        setSelectedSuggestion(id);
        setOpenApprove(true)
    };

    const handleDeny = (id) => {
        setSelectedSuggestion(id);
        setOpenReject(true)
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        setSelectedStatus(value);
        fetchSuggestions(1, searchTerm, value);
    };

    const fetchSuggestions = async (page = 1, search = '', selectedStatus) => {
        const res = await getSuggestionsData(page, search, selectedStatus);
        setData(res.data.payload.list_of_request);
        setTotalPages(res.data.payload.total_pages);
    };

    const debouncedfetchSuggestions = useCallback(
        debounce((value) => {
            fetchSuggestions(1, value);
            setPage(1);
        }, 500),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedfetchSuggestions(value);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        fetchSuggestions(page, searchTerm, selectedStatus);
    }, [page]);

    const openDialog = (suggestion) => {
        setSelectedSuggestion(suggestion);
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setSelectedSuggestion(null);
        setIsDialogOpen(false);
    };

    const handleApproveSubmit = async () => {
        setOpenApprove(false);
        const payload = {
            "status": 'approved',
        }
        await approveRejectSuggestion(selectedSuggestion, payload);
        fetchSuggestions(page, searchTerm, selectedStatus);
    };

    const handleRejectSubmit = async () => {
        if (!reason.trim()) {
            toast.error("Please enter a rejection reason.");
            return;
        }
        const payload = {
            "status": 'rejected',
            "reason": reason
        }
        setOpenReject(false);
        await approveRejectSuggestion(selectedSuggestion, payload);
        fetchSuggestions(page, searchTerm, selectedStatus);
        setReason("")
    };

    return (
        <div>
            <Breadcrumb title="Suggestions" />
            <div className="card h-100 p-0 radius-12 mt-24">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
                        <form className="navbar-search">
                            <input
                                type="text"
                                className="bg-base h-40-px"
                                name="search"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </form>
                        <select
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                            value={selectedStatus}
                            onChange={handleStatusChange}
                        >
                            <option value="" disabled>Select Status</option>
                            <option value="">All</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>
                <div className="card-body p-24">
                    {loading ? (
                        <CommonLoader />
                    ) : (
                        <>
                            <div className="table-responsive scroll-sm">
                                <table className="table bordered-table sm-table mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-start">User</th>
                                            <th className="text-center">Phone Number.</th>
                                            <th className="text-center">Created At</th>
                                            <th className="text-center">Status</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.length > 0 ? (
                                            data.map((item) => (
                                                <tr key={item?._id}>
                                                    <td className="text-start">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Avatar
                                                                src={item?.user_id?.user_id?.profile_photo}
                                                                alt=""
                                                                className="w-40-px h-40-px rounded-circle me-12"
                                                            />
                                                            <div className="d-flex flex-column align-items-start">
                                                                <span className="text-md text-secondary-light fw-medium">
                                                                    {item?.user_id?.user_name || "-"}
                                                                </span>
                                                                <span className="text-sm text-muted">
                                                                    {item?.user_id?.email || "-"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">{item?.user_id?.phone_number || "-"}</td>
                                                    <td className="text-center">
                                                        {dayjs(item?.createdAt).format('DD MMM YYYY hh:mm A')}
                                                    </td>
                                                    <td className="text-center">
                                                        <StatusBadge status={item?.status} />
                                                    </td>
                                                    <td className="text-center d-flex gap-2 justify-content-center align-items-center">
                                                        {isActionAllowed('Suggestions', 'view') ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => openDialog(item)}
                                                                className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon
                                                                    icon="majesticons:eye-line"
                                                                    className="icon text-xl"
                                                                />
                                                            </button>
                                                        ) : null}
                                                        {isActionAllowed('Suggestions', 'edit') ? (
                                                            <button
                                                                onClick={() => handleApprove(item._id)}
                                                                className="bg-success-focus bg-hover-success-200 text-success-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon icon="mdi:check-bold" className="text-xl" />
                                                            </button>
                                                        ) : null}
                                                        {isActionAllowed('Suggestions', 'edit') ? (
                                                            <button
                                                                onClick={() => handleDeny(item._id)}
                                                                className="bg-danger-focus bg-hover-danger-200 text-danger-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon icon="mdi:close-thick" className="text-xl" />
                                                            </button>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">No Suggestions Found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                                <span>Page {page} of {total_pages}</span>
                                <Pagination
                                    total_pages={total_pages}
                                    current_page={page}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* MUI Dialog with Bootstrap Card */}
            <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <div className="card mb-0">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">View Suggestion</h6>
                        <button type="button" className="btn btn-sm btn-light p-1" onClick={closeDialog}>
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="card-body">
                        <div className="row gy-3">
                            <div className="col-md-6">
                                <label className="form-label">Suggestion</label>
                                <div>{selectedSuggestion?.suggestion}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={openApprove}
                onClose={() => setOpenApprove(false)}
                maxWidth={false} // Disable MUI's default maxWidth
                fullWidth={false}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        width: '350px', // Custom width here
                    }
                }}
            >
                <div className="card mb-0">
                    <div className="m-2 d-flex justify-content-end align-items-center">

                        <button type="button" className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center" onClick={() => setOpenApprove(false)}>
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="card-body text-center pb-24">
                        <h6 className="card-title mb-0">Approve Suggestion</h6>
                        <p className="text-center mt-3">
                            Are you sure want to approve this suggestion ?
                        </p>
                        <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                            <button onClick={handleApproveSubmit}
                                className="btn btn-success border border-success-600 text-md px-48 py-12 radius-8">
                                Approve
                            </button>
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={openReject}
                onClose={() => setOpenReject(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <div className="card mb-0">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Reject Suggestion</h6>
                        <button type="button" className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center" onClick={() => setOpenReject(false)}>
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="card-body">
                        <p className="">

                            <textarea
                                className="form-control"
                                rows="4"
                                placeholder="Write reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </p>
                        <div className="d-flex justify-content-center align-items-center mt-3">
                            <button onClick={handleRejectSubmit} className="btn btn-danger border border-danger-600 text-md px-48 py-12 radius-8">
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default SuggestionsPage;
